import React from 'react';
import { CalendarDays, TrendingUp, Percent, Clock, Wallet, PiggyBank, AlertCircle, Home, Heart, GraduationCap, HeartHandshake } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета налогового вычета
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Иконки для разных типов вычетов
  const deductionIcons = {
    property: <Home className="w-4 h-4"/>,
    mortgage: <Home className="w-4 h-4"/>,
    medical: <Heart className="w-4 h-4"/>,
    education: <GraduationCap className="w-4 h-4"/>,
    investment: <PiggyBank className="w-4 h-4"/>,
    charity: <HeartHandshake className="w-4 h-4"/>
  };

  // Получение названия типа вычета
  const getDeductionName = (typeId) => {
    const deductionNames = {
      property: 'Имущественный вычет',
      mortgage: 'Вычет по ипотечным процентам',
      medical: 'Вычет на лечение',
      education: 'Вычет на обучение',
      investment: 'Инвестиционный вычет (ИИС)',
      charity: 'Вычет на благотворительность',
      comprehensive: 'Комплексный расчет'
    };
    return deductionNames[typeId] || 'Налоговый вычет';
  };

  return (
    <ToolResult title="Результаты расчета налогового вычета">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общая сумма возврата НДФЛ */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Wallet className="w-4 h-4"/>
            Общий возврат НДФЛ
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatCurrency(results.totalTaxReturn)}
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            {results.yearsToComplete > 1 
              ? `Полный возврат займет ${results.yearsToComplete} ${pluralizeYears(results.yearsToComplete)}`
              : 'Полный возврат в течение года'}
          </p>
        </div>

        {/* Сумма вычета */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Общая сумма вычета
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatCurrency(results.totalAvailableDeduction)}
            </span>
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              (Ставка НДФЛ: {formData.taxRate}%)
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            {formData.deductionType === 'comprehensive' 
              ? 'Комплексный расчет по всем типам вычетов' 
              : `${getDeductionName(formData.deductionType)}`}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Годовой доход" 
          value={formatCurrency(parseFloat(formData.incomeAmount))} 
          color="emerald" 
          icon={<Wallet className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Годовой НДФЛ" 
          value={formatCurrency(results.annualTaxPaid)} 
          color="red" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        {results.spouseAnnualTaxPaid > 0 && (
          <ResultItem 
            label="Годовой НДФЛ супруга(и)" 
            value={formatCurrency(results.spouseAnnualTaxPaid)} 
            color="red" 
            icon={<Percent className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Общий срок получения вычета" 
          value={`${results.yearsToComplete} ${pluralizeYears(results.yearsToComplete)}`} 
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Год начала получения вычета" 
          value={results.startYear} 
          icon={<CalendarDays className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Год окончания получения вычета" 
          value={results.startYear + results.yearsToComplete - 1} 
          icon={<CalendarDays className="w-4 h-4"/>} 
        />
        
        {formData.deductionType === 'comprehensive' ? (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Детализация по типам вычетов:</h4>
            {Object.keys(results.deductionsByType).map(type => {
              const deduction = results.deductionsByType[type];
              if (deduction.isTypeB) return null; // Пропускаем тип Б ИИС, для него нет возврата
              
              return (
                <div key={type} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                  <div className="flex items-center">
                    {deductionIcons[type] && (
                      <span className="mr-2 text-gray-500 dark:text-gray-400">
                        {deductionIcons[type]}
                      </span>
                    )}
                    <span className="text-sm text-gray-800 dark:text-gray-200">{deduction.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(deduction.taxReturn)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      от {formatCurrency(deduction.baseAmount)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          results.deductionsByType && results.deductionsByType[formData.deductionType] && (
            <ResultItem 
              label={`Сумма вычета: ${results.deductionsByType[formData.deductionType].name}`} 
              value={formatCurrency(results.deductionsByType[formData.deductionType].deductionAmount)} 
              color="blue" 
            />
          )
        )}
      </div>

      {/* Информация о социальных вычетах */}
      {(formData.deductionType === 'medical' || formData.deductionType === 'education' || 
        formData.deductionType === 'charity' || formData.deductionType === 'comprehensive') && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Важно о социальных вычетах</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Социальные вычеты (на лечение, обучение, благотворительность) 
                <strong className="text-amber-600 dark:text-amber-400"> не переносятся на следующие годы</strong>. 
                Рекомендуется подавать заявление на них в тот год, когда были произведены расходы.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Информация о максимальном вычете */}
      {(formData.deductionType === 'property' || formData.deductionType === 'mortgage') && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Home className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Имущественный вычет</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Имущественный вычет предоставляется один раз в жизни. Максимальная сумма к вычету: 2 млн ₽ 
                (возврат до 260 000 ₽). Дополнительно можно получить вычет по ипотечным процентам до 3 млн ₽ 
                (возврат до 390 000 ₽).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Информация о получении вычета */}
      <div className="mt-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800">
        <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2">Как получить налоговый вычет:</h4>
        <div className="space-y-2 text-sm text-emerald-600 dark:text-emerald-400">
          <p>
            1. <strong>Подготовьте документы</strong> - декларация 3-НДФЛ, справка 2-НДФЛ, документы по расходам
          </p>
          <p>
            2. <strong>Подайте заявление</strong> - через личный кабинет налогоплательщика, МФЦ или почтой
          </p>
          <p>
            3. <strong>Дождитесь проверки</strong> - камеральная проверка длится до 3 месяцев
          </p>
          <p>
            4. <strong>Получите возврат</strong> - в течение месяца после положительного решения
          </p>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const pluralizeYears = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ResultsOverview;